import React, { useEffect, useState } from "react";
import "./OnBoardingPostType.scss";
import { useSearchParams } from "react-router-dom";
import { CommonEnums } from "../../../../enums/common.enums";
import Modal from "@mui/material/Modal";
import { PropertyApi } from "../../../../utils/api/Property";
import Loading from "../../../loading/Loading";
import toast from "react-hot-toast";
import { TemplatesApi } from "../../../../utils/api/Templates";

interface OnBoardingPostTypeProps {
  selectedType: string | null;
  onSelectType: (type: string | null) => void;
  onContinue: () => void;
}

const OnBoardingPostType = ({
  selectedType,
  onSelectType,
  onContinue,
}: OnBoardingPostTypeProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);

  useEffect(() => {
    const paramType = searchParams.get("post_type");
    if (paramType && paramType !== selectedType) {
      onSelectType(paramType);
    }
  }, [searchParams, selectedType, onSelectType]);

  const handleCheckOnBoardingTemplates = async (postType: string) => {
    setModalOpen(true);
    setIsProcessing(true);

    const subscriptionType = searchParams.get("subscription_type") ?? "";
    const listingType = "sale";

    try {
      const res = await TemplatesApi.getTemplateConfigs({
        subscription_type: subscriptionType,
        listing_type: listingType,
      });

      const postTypeData = res?.data?.data?.[postType];

      if (!postTypeData || !Array.isArray(postTypeData.templates)) {
        return fail();
      }

      const validTemplates = postTypeData.templates.filter((t: any) => {
        return (
          t.post_type === postType &&
          t.design_type === "onboarding_post" &&
          t.listing_type?.toLowerCase() === listingType.toLowerCase()
        );
      });

      const hasConfig = validTemplates.some((t: any) => !!t.config);

      if (!hasConfig) {
        return fail();
      }

      return true;
    } catch (err) {
      console.error("Auction template check failed", err);
      return fail();
    }

    function fail() {
      setModalOpen(false);
      setIsProcessing(false);
      setTemplateModalOpen(true);
      return false;
    }
  };

  const handleSelect = async (type: string) => {
    const ok = await handleCheckOnBoardingTemplates(type);
    if (!ok) return;

    try {
      setModalOpen(true);
      setIsProcessing(true);

      const payload = {
        post_type: type,
        finalize: true,
      };

      const res = await PropertyApi.createOnboardingPost(payload);

      if (res?.data?.success) {
        const newId = res.data.data?._id;

        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("onboarding_post_id", newId);
          next.set("post_type", type);
          next.set("step", CommonEnums.onBoardingPostTabs.create_post.key);
          return next;
        });

        onSelectType(type);
      } else {
        toast.error(res?.data?.message || "Failed to create onboarding post!");
      }
    } catch (error) {
      console.error("Failed to create onboarding post", error);
      toast.error("Something went wrong while creating the onboarding post!");
    } finally {
      setIsProcessing(false);
      setModalOpen(false);
    }
  };

  const handleCancel = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("post_type");
    setSearchParams(nextParams);
    onSelectType(null);
  };

  const postTypeData = [
    {
      id: "1",
      icon: "/images/workspace/image.svg",
      title: "Image Post",
      type: CommonEnums.postType.image_post.key,
    },
    {
      id: "2",
      icon: "/images/workspace/image.svg",
      title: "Story Post",
      type: CommonEnums.postType.story_post.key,
    },
    {
      id: "3",
      icon: "/images/workspace/video.svg",
      title: "Video Post",
      type: CommonEnums.postType.video_post.key,
    },
    {
      id: "4",
      icon: "/images/workspace/video.svg",
      title: "Reel Post",
      type: CommonEnums.postType.reel_post.key,
    },
  ];

  return (
    <div className="property-post-type-container">
      <h3 className="step-title">Select Post Type to Continue</h3>

      <div className="template-type-list-container">
        {postTypeData.map((data) => (
          <div
            key={data.type}
            className={`template-type-box ${
              selectedType === data.type ? "selected" : ""
            } ${
              data.type === CommonEnums.postType.story_post.key
                ? "story-post"
                : data.type === CommonEnums.postType.reel_post.key
                ? "reel-post"
                : data.type === CommonEnums.postType.image_post.key
                ? "image-post"
                : data.type === CommonEnums.postType.video_post.key
                ? "video-post"
                : ""
            }`}
            onClick={() => handleSelect(data.type)}
          >
            <div className="template-image-wrapper">
              <img src={data.icon} alt={data.title} />
            </div>
            <div className="template-title-below">
              <p>{data.title}</p>
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={modalOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setModalOpen(false);
          }
        }}
        aria-labelledby="processing-modal"
        className="add-property-modal styled-modal"
        disableEscapeKeyDown
      >
        <div className="modal-center-wrapper">
          <div className="loading-content-wrapper">
            <div className="loading-container">
              <div className="spinner" />
              <p className="loading-text">
                Processing your request
                <span className="desktop-break">
                  <br />
                </span>
                Please wait...
              </p>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={templateModalOpen}
        onClose={() => {}}
        disableEscapeKeyDown
        className="add-property-modal styled-modal"
      >
        <div className="modal-center-wrapper">
          <div className="template-warning-modal">
            <img
              src="/images/HistoryTemplate.svg"
              alt="Pending Icon"
              className="pending-icon"
            />

            <h2>Template configuration is pending!</h2>

            <p className="description-text">
              Please select the design templates to create the onboarding post.
            </p>

            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setTemplateModalOpen(false)}
              >
                Cancel
              </button>

              <button
                className="select-btn"
                onClick={() => {
                  setTemplateModalOpen(false);
                  window.location.href = "/design-templates";
                }}
              >
                Select templates
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OnBoardingPostType;
